import { Button, Tag, Card, TableProps, Empty } from 'antd';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import {
	DataTableMaterialOrder,
	OrderMaterial,
} from '../../interfaces/Material.Interface';
import { SorterResult } from 'antd/es/table/interface';
import type { HTMLAttributes } from 'react';

import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';
import { getTagColor, normalizeStatusDelivered } from '../../utils/index.ts';
import BuildingTag from '../BuildingTag';

const MaterialOrdersTable = (props: DataTableMaterialOrder) => {
	const onChange: TableProps<OrderMaterial>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		const sorterColumn = (sorter as SorterResult<OrderMaterial>)?.column;

		if (extra.action === 'sort' && sorterColumn === undefined) {
			props.onSort(undefined);
			return;
		}

		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult<OrderMaterial>)?.order;
			// const columnKey = (sorter as SorterResult<OrderMaterial>)?.columnKey;
			const column =
				orderType === 'ascend' || orderType === undefined
					? sorterColumn?.key
					: '-' + sorterColumn?.key;
			if (column) {
				props.onSort(column?.toString());
			}
		}
	};
	const tableColumns = [
		{
			title: 'Vendor',
			render: (record: OrderMaterial) => `${record?.vendor?.display_name}`,
			key: 'vendor__display_name',
			sorter: true,
		},
		{
			title: 'Building',
			render: (record: OrderMaterial) => (
				<BuildingTag name={record.building?.name} showIcon={false} />
			),
			key: 'building',
		},
		{
			title: 'Vendor Order Number',
			render: (record: OrderMaterial) =>
				`${record?.vendor_order_number ? record?.vendor_order_number : ''}`,
			key: 'vendor_order_number',
			sorter: true,
		},
		{
			title: 'Created',
			render: (record: OrderMaterial) =>
				dayjs(record?.created_time).format('MMM DD YYYY'),
			key: 'created_time',
			sorter: true,
		},
		{
			title: 'Status',
			render: (record: OrderMaterial) => (
				<span
					data-testid="fastlane-material-order-status"
					data-material-order-id={record.id}
					data-material-order-status={record.status}
				>
					{record.status === "In Progress" ?
						<Tag color={getTagColor("Draft")}>In Progress</Tag>
						: <Tag color={getTagColor(record.status)}>{normalizeStatusDelivered(record.status)}</Tag>
					}
				</span>
			),
			key: 'actions',
			sorter: (a: OrderMaterial, b: OrderMaterial) =>
				a.status.localeCompare(b.status),
		},
		{
			title: '',
			render: (record: OrderMaterial) => (
				<>
				{
					record.status !="Draft" && record.status !="In Progress"
				? (
					<Link
						to={`/material/orders/${record?.id}`}>
						<Button>Details</Button>
					</Link>
				): null

				}
					{(record.status === 'Draft' || record.status === 'In Progress') && (
						<Link to={`/material/orders/add/${record?.id}/`} className={'mx-2'}>
							<Button data-testid="fastlane-material-order-edit-btn">Edit</Button>
						</Link>
					)}
				</>
			),
		},
	];

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no material orders to be displayed.
					</span>
				}
			/>
		);
	};

	return (
		<Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
			<div
				data-testid="fastlane-material-orders-table"
				data-ready={!props.isLoading ? 'true' : 'false'}
				style={{ display: 'flex', flexDirection: 'column' }}
			>
				<CustomResponsiveTable
					columns={tableColumns}
					data={props.data}
					loading={props.isLoading}
					rowKey="id"
					onRow={(record) => ({
						'data-testid': 'fastlane-material-order-row',
						'data-material-order-id': record.id,
					} as HTMLAttributes<HTMLElement>)}
					onChange={onChange}
					pagination={{
						pageSize: 20,
						current: props.currentPage,
						pageSizeOptions: [],
						total: props.total,
						onChange: (page) => {
							props.onChangePage(page);
						},
						defaultPageSize: 20,
						showSizeChanger: false,
					}}
					emptyTable={emptyTable}
				/>
			</div>
		</Card>
	);
};

export default MaterialOrdersTable;
